import React, { useState, useEffect } from "react";
import { getLessonTypeStyle } from "../../utils/colorUtils";
import "../../assets/style/LessonTypes.css";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogActions,
  Autocomplete,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
  useMediaQuery,
  Stack,
  Card,
  CardContent,
  Tabs,
  Tab,
} from "@mui/material";
import { 
  Delete as DeleteIcon, 
  Edit as EditIcon, 
  Add as AddIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  LocationOn as LocationIcon,
  VisibilityOff as VisibilityOffIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";
import "../../assets/style/Timetable.css";
import CreationReportDialog from "./CreationReportDialog";

const ConstLessonManagement = () => {
  // State for const lessons
  const [constLessons, setConstLessons] = useState([]);
  const [selectedConstLesson, setSelectedConstLesson] = useState(null);
  const [constLessonDialogOpen, setConstLessonDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // State for creating new const lesson
  const [createLessonDialogOpen, setCreateLessonDialogOpen] = useState(false);
  const [lessonTypes, setLessonTypes] = useState([]);
  const [lessonTypeLocations, setLessonTypeLocations] = useState([]);
  const [newLessonData, setNewLessonData] = useState({
    lesson_type_id: '',
    trainer_id: null,
    day_of_week: 0, // 0 = Monday
    hour: 10,
    minute: 0,
    duration_minutes: 50,
    is_free: false,
    payment_on_site: false,
    payment_only_online: false,
    online_payment_amount: null,
    on_site_payment_amount: null,
    sign_up_disabled: false
  });

  // State for editing existing const lesson
  const [editLessonDialogOpen, setEditLessonDialogOpen] = useState(false);
  const [editLessonData, setEditLessonData] = useState({
    id: null,
    lesson_type_id: '',
    trainer_id: null,
    day_of_week: 0,
    hour: 10,
    minute: 0,
    duration_minutes: 50,
    is_free: false,
    payment_on_site: false,
    payment_only_online: false,
    online_payment_amount: null,
    on_site_payment_amount: null,
    sign_up_disabled: false
  });

  // State for managing users in const lesson
  const [constLessonUsers, setConstLessonUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState(null);
  
  // State for creating actual lesson from const lesson
  const [createActualLessonDialogOpen, setCreateActualLessonDialogOpen] = useState(false);
  const [actualLessonData, setActualLessonData] = useState({
    lesson_dates: [],
    user_ids: [],
    is_free: null,
    payment_on_site: null,
    payment_only_online: null,
    online_payment_amount: null,
    on_site_payment_amount: null
  });
  const [availableDates, setAvailableDates] = useState([]);
  const [creationProgress, setCreationProgress] = useState(null); // { total, completed, succeeded, failed, results }
  const [trainers, setTrainers] = useState([]);
  const [lessonUsersForCreation, setLessonUsersForCreation] = useState([]); // Users to be included in the lesson
  
  // State for pass management
  const [userPasses, setUserPasses] = useState({}); // userId -> [passes]
  const [userPassSelections, setUserPassSelections] = useState({}); // userId -> selectedPassId

  // Loading states
  const [isCreatingLesson, setIsCreatingLesson] = useState(false);
  const [isEditingLesson, setIsEditingLesson] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isUpdatingUser, setIsUpdatingUser] = useState({});
  const [isCreatingActualLesson, setIsCreatingActualLesson] = useState(false);
  // Tracks which const lesson is currently being prepared ("Do Grafiku" click).
  // null when nothing is loading; otherwise it holds the const_lesson.id whose
  // dialog is being prepared. Both "Do Grafiku" buttons consult this.
  const [openingConstLessonId, setOpeningConstLessonId] = useState(null);

  // Per-batch report shown after "Do Grafiku" submit. Each entry corresponds
  // to one created lesson and matches the shape expected by
  // CreationReportDialog: { lesson_id, lesson_start, lesson_type_name, results }.
  const [creationReports, setCreationReports] = useState([]);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportDialogMode, setReportDialogMode] = useState("committed");
  // Payload captured during the dry-run preview so the follow-up "confirm"
  // commit uses the exact same request the admin reviewed.
  const [pendingBatchPayload, setPendingBatchPayload] = useState(null);
  // Spinner state for the "Potwierdź i utwórz" button while commit-lessons runs.
  const [commitBusy, setCommitBusy] = useState(false);
  
  // Error and success messages
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(0); // 0 = "All"
  const [createLocationFilter, setCreateLocationFilter] = useState(''); // '' = all locations
  const [editLocationFilter, setEditLocationFilter] = useState(''); // '' = all locations

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchLessonTypes();
    fetchLessonTypeLocations();
    fetchAllUsers();
    fetchTrainers();
    fetchConstLessons();
  }, []);

  const fetchLessonTypes = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type/`,
        { headers }
      );
      if (response.status === 200 && Array.isArray(response.data.objects)) {
        setLessonTypes(response.data.objects);
      }
    } catch (error) {
      console.error("Error fetching lesson types:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do pobrania typów lekcji");
      }
    }
  };

  const fetchLessonTypeLocations = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type-locations/`,
        { headers }
      );
      if (response.status === 200) {
        setLessonTypeLocations(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching lesson type locations:", error);
      setLessonTypeLocations([]);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user`,
        { headers }
      );
      if (response.status === 200 && Array.isArray(response.data.objects)) {
        setAllUsers(response.data.objects);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do pobrania użytkowników");
      }
    }
  };

  const fetchTrainers = async () => {
    try {
      const headers = await getAuthHeaders();
      const trainersResponse = await axios.get(`${process.env.REACT_APP_BACKEND_ADDRESS}/trainers/`, {
        headers
      });

      if (trainersResponse.status === 200) {
        setTrainers(Array.isArray(trainersResponse.data) ? trainersResponse.data : trainersResponse.data.objects || []);
      }
    } catch (error) {
      console.error("Error fetching trainers:", error);
      setTrainers([]);
    }
  };

  const fetchUserPasses = async (userIds, lessonTypeId = null) => {
    if (userIds.length === 0) return;
    
    try {
      // Fetch users with passes, filtered by lesson type if specified
      const params = {};
      if (lessonTypeId) {
        params.lesson_type_id = lessonTypeId;
      }
      
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user/passes`,
        { 
          headers,
          params: params
        }
      );

      if (response.status === 200) {
        const allUsersWithPasses = response.data.users;
        const passesMap = {};
        const defaultSelections = {};
        
        // Build a map of userId -> passes for users in our lesson
        allUsersWithPasses.forEach(userWithPasses => {
          if (userWithPasses.passes && userWithPasses.passes.length > 0) {
            const userId = userWithPasses.passes[0]?.user_id;
            if (userId && userIds.includes(userId)) {
              // Filter passes to only include active ones with remaining lessons
              const activePasses = userWithPasses.passes.filter(pass => {
                const validUntil = new Date(pass.valid_until);
                const now = new Date();
                const hasRemainingLessons = pass.lesson_categories_for_pass?.some(category => category.remaining_lessons > 0);
                
                return validUntil > now && pass.active && hasRemainingLessons;
              });
              
              passesMap[userId] = activePasses;
              
              // No pass is pre-selected — the dropdown defaults to
              // "Automatycznie", so the backend auto-picks eligible passes per
              // date and can spread consumption across several of the user's
              // passes over the recurring dates.
            }
          }
        });
        
        // Ensure all userIds are represented in the map, even if they have no passes
        userIds.forEach(userId => {
          if (!(userId in passesMap)) {
            passesMap[userId] = [];
          }
        });
        
        setUserPasses(passesMap);
        setUserPassSelections(prev => ({
          ...prev,
          ...defaultSelections
        }));
      }
    } catch (error) {
      console.error("Error fetching user passes:", error);
      setUserPasses({});
    }
  };

  const fetchConstLessons = async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/const-lessons/`,
        { headers }
      );
      if (response.status === 200) {
        setConstLessons(response.data);
      }
    } catch (error) {
      console.error("Error fetching const lessons:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do pobrania stałych lekcji");
      } else {
        setError("Nie udało się pobrać stałych lekcji");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchConstLessonUsers = async (constLessonId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/const-lessons/${constLessonId}/users`,
        { headers }
      );
      if (response.status === 200) {
        // The backend now returns user details directly
        setConstLessonUsers(response.data);
      }
    } catch (error) {
      console.error("Error fetching const lesson users:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do pobrania użytkowników lekcji");
      }
    }
  };


  const handleCreateConstLesson = async () => {
    try {
      setIsCreatingLesson(true);
      setError('');
      
      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/const-lessons/`,
        newLessonData,
        { headers }
      );

      if (response.status === 200) {
        setSuccess('Stała lekcja została utworzona pomyślnie!');
        setCreateLessonDialogOpen(false);
        setNewLessonData({
          lesson_type_id: '',
          trainer_id: null,
          day_of_week: 0,
          hour: 10,
          minute: 0,
          duration_minutes: 50,
          is_free: false,
          payment_on_site: false,
          payment_only_online: false,
          sign_up_disabled: false
        });
        await fetchConstLessons();
      }
    } catch (error) {
      console.error("Error creating const lesson:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do utworzenia stałej lekcji");
      } else if (error.response?.data?.detail) {
        setError(`Nie udało się utworzyć stałej lekcji: ${error.response.data.detail}`);
      } else {
        setError('Nie udało się utworzyć stałej lekcji');
      }
    } finally {
      setIsCreatingLesson(false);
    }
  };

  const handleOpenEditLesson = (constLesson) => {
    setEditLocationFilter(constLesson.lesson_type?.location?.id || '');
    setEditLessonData({
      id: constLesson.id,
      lesson_type_id: constLesson.lesson_type_id,
      trainer_id: constLesson.trainer_id || null,
      day_of_week: constLesson.day_of_week,
      hour: constLesson.hour,
      minute: constLesson.minute,
      duration_minutes: constLesson.duration_minutes,
      is_free: constLesson.is_free || false,
      payment_on_site: constLesson.payment_on_site || false,
      payment_only_online: constLesson.payment_only_online || false,
      online_payment_amount: constLesson.online_payment_amount || null,
      on_site_payment_amount: constLesson.on_site_payment_amount || null,
      sign_up_disabled: constLesson.sign_up_disabled || false
    });
    setEditLessonDialogOpen(true);
  };

  const handleUpdateConstLesson = async () => {
    try {
      setIsEditingLesson(true);
      setError('');

      const { id, ...updateData } = editLessonData;

      const headers = await getAuthHeaders();
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/const-lessons/${id}`,
        updateData,
        { headers }
      );

      if (response.status === 200) {
        setSuccess('Stała lekcja została zaktualizowana pomyślnie!');
        setEditLessonDialogOpen(false);
        setEditLessonData({
          id: null,
          lesson_type_id: '',
          trainer_id: null,
          day_of_week: 0,
          hour: 10,
          minute: 0,
          duration_minutes: 50,
          is_free: false,
          payment_on_site: false,
          payment_only_online: false,
          sign_up_disabled: false
        });
        await fetchConstLessons();
      }
    } catch (error) {
      console.error("Error updating const lesson:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do aktualizacji stałej lekcji");
      } else if (error.response?.data?.detail) {
        setError(`Nie udało się zaktualizować stałej lekcji: ${error.response.data.detail}`);
      } else {
        setError('Nie udało się zaktualizować stałej lekcji');
      }
    } finally {
      setIsEditingLesson(false);
    }
  };

  const handleDeleteConstLesson = async (constLessonId) => {
    if (window.confirm('Czy na pewno chcesz usunąć tę stałą lekcję?')) {
      try {
        const headers = await getAuthHeaders();
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/const-lessons/${constLessonId}`,
          { headers }
        );
        setSuccess('Stała lekcja została usunięta');
        await fetchConstLessons();
      } catch (error) {
        console.error("Error deleting const lesson:", error);
        if (error.response?.status === 403 || error.response?.status === 401) {
          setError("Brak uprawnień do usunięcia stałej lekcji");
        } else {
          setError('Nie udało się usunąć stałej lekcji');
        }
      }
    }
  };

  const handleViewUsers = async (constLesson) => {
    setSelectedConstLesson(constLesson);
    setConstLessonDialogOpen(true);
    
    // Ensure users are loaded before fetching const lesson users
    if (allUsers.length === 0) {
      await fetchAllUsers();
    }
    
    await fetchConstLessonUsers(constLesson.id);
  };

  const handleAddUserToConstLesson = async () => {
    if (!selectedUserToAdd || !selectedConstLesson) return;

    try {
      setIsAddingUser(true);
      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/const-lessons/${selectedConstLesson.id}/users`,
        { user_id: selectedUserToAdd.id, is_signed_for_this_time: true },
        { headers }
      );

      if (response.status === 200) {
        setSuccess('Użytkownik został dodany do stałej lekcji');
        setAddUserDialogOpen(false);
        setSelectedUserToAdd(null);
        await fetchConstLessonUsers(selectedConstLesson.id);
      }
    } catch (error) {
      console.error("Error adding user to const lesson:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do dodania użytkownika");
      } else if (error.response?.status === 400) {
        setError('Użytkownik jest już na liście tej stałej lekcji');
      } else {
        setError('Nie udało się dodać użytkownika do stałej lekcji');
      }
    } finally {
      setIsAddingUser(false);
    }
  };

  const handleRemoveUserFromConstLesson = async (userId) => {
    if (window.confirm('Czy na pewno chcesz usunąć tego użytkownika ze stałej listy?')) {
      try {
        const headers = await getAuthHeaders();
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/const-lessons/${selectedConstLesson.id}/users/${userId}`,
          { headers }
        );
        setSuccess('Użytkownik został usunięty ze stałej listy');
        await fetchConstLessonUsers(selectedConstLesson.id);
      } catch (error) {
        console.error("Error removing user from const lesson:", error);
        if (error.response?.status === 403 || error.response?.status === 401) {
          setError("Brak uprawnień do usunięcia użytkownika");
        } else {
          setError('Nie udało się usunąć użytkownika ze stałej listy');
        }
      }
    }
  };

  const handleToggleUserSign = async (userId) => {
    try {
      setIsUpdatingUser(prev => ({ ...prev, [userId]: true }));
      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/const-lessons/${selectedConstLesson.id}/users/${userId}/toggle-sign`,
        {},
        { headers }
      );
      
      if (response.status === 200) {
        setSuccess(response.data.message);
        await fetchConstLessonUsers(selectedConstLesson.id);
      }
    } catch (error) {
      console.error("Error toggling user sign:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do zmiany statusu użytkownika");
      } else {
        setError('Nie udało się zmienić statusu zapisu użytkownika');
      }
    } finally {
      setIsUpdatingUser(prev => ({ ...prev, [userId]: false }));
    }
  };

  const getLessonTypeName = (lessonTypeId, constLesson = null) => {
    // First try to get from constLesson.lesson_type if available
    if (constLesson && constLesson.lesson_type) {
      return constLesson.lesson_type.friendly_name || constLesson.lesson_type.name;
    }
    // Fallback to finding in lessonTypes array
    const lessonType = lessonTypes.find(lt => lt.id === lessonTypeId);
    return lessonType?.friendly_name || lessonType?.name || `ID: ${lessonTypeId}`;
  };

  const getLessonTypeWithLocation = (lessonTypeId, constLesson = null) => {
    let lessonType;
    
    // First try to get from constLesson.lesson_type if available
    if (constLesson && constLesson.lesson_type) {
      lessonType = constLesson.lesson_type;
    } else {
      // Fallback to finding in lessonTypes array
      lessonType = lessonTypes.find(lt => lt.id === lessonTypeId);
    }
    
    if (!lessonType) return `ID: ${lessonTypeId}`;
    
    const locationName = lessonType.location ? lessonType.location.name : 'Brak lokalizacji';
    const displayName = lessonType.friendly_name || lessonType.name;
    return `${displayName} (${locationName})`;
  };

  const formatDateTime = (dateTimeString) => {
    return new Date(dateTimeString).toLocaleString('pl-PL');
  };

  const formatConstLessonTime = (constLesson) => {
    const dayNames = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
    const dayName = dayNames[constLesson.day_of_week] || `Dzień ${constLesson.day_of_week}`;
    return `${dayName} o ${constLesson.hour.toString().padStart(2, '0')}:${constLesson.minute.toString().padStart(2, '0')} (${constLesson.duration_minutes} min)`;
  };

  // Derive selected location ID from index
  const selectedLocationId = selectedLocationIndex === 0 ? null : lessonTypeLocations[selectedLocationIndex - 1]?.id ?? null;

  // Filter const lessons by selected location
  const filteredConstLessons = selectedLocationId === null
    ? constLessons
    : constLessons.filter(cl => cl.lesson_type?.location?.id === selectedLocationId);

  // Helper function to organize const lessons into timetable structure
  const organizeConstLessonsForTimetable = () => {
    const timetable = {};
    const weekDayMap = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
    
    filteredConstLessons.forEach(constLesson => {
      const timeKey = `${constLesson.hour.toString().padStart(2, '0')}:${constLesson.minute.toString().padStart(2, '0')}`;
      
      if (!timetable[timeKey]) {
        timetable[timeKey] = [];
      }
      
      // Convert const lesson to format expected by SingleConstLessonHour
      const formattedLesson = {
        ...constLesson,
        week_day: weekDayMap[constLesson.day_of_week],
        start: {
          hour: constLesson.hour,
          minute: constLesson.minute
        },
        end: {
          hour: Math.floor((constLesson.hour * 60 + constLesson.minute + constLesson.duration_minutes) / 60) % 24,
          minute: (constLesson.minute + constLesson.duration_minutes) % 60
        }
      };
      
      timetable[timeKey].push(formattedLesson);
    });
    
    return timetable;
  };

  // Component to render a single hour row in the const lessons timetable
  const SingleConstLessonHour = ({ hour, timeSlot }) => {
    const weekDayMap = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
    const timeSlots = [];
    
    const addLeadingZeros = (num) => String(num).padStart(2, "0");

    for (let i = 0; i < 7; i++) {
      const dayName = weekDayMap[i];
      const lessonsInDay = hour ? hour.filter(lesson => lesson.week_day === dayName) : [];

      if (lessonsInDay.length > 0) {
        const lessonGroup = lessonsInDay.map((constLesson) => {
          const start = `${addLeadingZeros(constLesson.start.hour)}:${addLeadingZeros(constLesson.start.minute)}`;
          const end = `${addLeadingZeros(constLesson.end.hour)}:${addLeadingZeros(constLesson.end.minute)}`;
          const maxPeople = constLesson.lesson_type?.max_people || 'Brak limitu';

          return (
            <Box 
              key={constLesson.id} 
              sx={{ 
                p: 2, 
                mb: 1,
                borderRadius: 2,
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease'
              }}
            >
              <Box
                onClick={() => handleViewUsers(constLesson)}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  mb: 1,
                  ...getLessonTypeStyle(constLesson.lesson_type?.bg_color),
                  '&:hover': {
                    opacity: 0.9
                  }
                }}
              >
                {getLessonTypeName(constLesson.lesson_type_id, constLesson)}
              </Box>
              
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, textAlign: 'center' }}>
                {start} - {end}
              </Typography>

              {!constLesson.lesson_type?.location && (
                <Chip
                  icon={<WarningIcon fontSize="small" />}
                  label="Brak lokalizacji"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ display: 'flex', mx: 'auto', mb: 0.5 }}
                />
              )}
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1.5 }}>
                Max osób: {maxPeople}
              </Typography>
              
              <Box display="flex" gap={0.5} flexWrap="wrap" justifyContent="center">
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewUsers(constLesson);
                  }}
                  startIcon={<PeopleIcon />}
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    px: 1,
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'white'
                    }
                  }}
                >
                  Użytkownicy
                </Button>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenCreateActualLesson(constLesson);
                  }}
                  startIcon={openingConstLessonId === constLesson.id
                    ? <CircularProgress size={14} color="inherit" />
                    : <AddIcon />}
                  disabled={openingConstLessonId === constLesson.id}
                  color="success"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    px: 1,
                    '&:hover': {
                      backgroundColor: 'success.light',
                      color: 'white'
                    }
                  }}
                >
                  Do Grafiku
                </Button>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditLesson(constLesson);
                  }}
                  color="primary"
                  size="small"
                  sx={{
                    p: 0.5,
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'white'
                    }
                  }}
                  title="Edytuj"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteConstLesson(constLesson.id);
                  }}
                  color="error"
                  size="small"
                  sx={{
                    p: 0.5,
                    '&:hover': {
                      backgroundColor: 'error.light',
                      color: 'white'
                    }
                  }}
                  title="Usuń"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          );
        });

        timeSlots.push(
          <td key={`lesson-group-${i}`} style={{ padding: '16px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
            {lessonGroup}
          </td>
        );
      } else {
        timeSlots.push(
          <td 
            key={`empty-${i}`} 
            style={{ 
              backgroundColor: '#f8f9fa', 
              height: '120px',
              border: '1px solid #e0e0e0'
            }} 
          />
        );
      }
    }

    return <tr>{timeSlots}</tr>;
  };

  // Helper function to get available dates for a specific weekday
  const getAvailableDatesForWeekday = (dayOfWeek) => {
    const dates = [];
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Get dates for current month and next month
    for (let monthOffset = 0; monthOffset <= 1; monthOffset++) {
      const targetMonth = currentMonth + monthOffset;
      const targetYear = targetMonth > 11 ? currentYear + 1 : currentYear;
      const adjustedMonth = targetMonth > 11 ? targetMonth - 12 : targetMonth;
      
      // Get the last day of the target month
      const lastDay = new Date(targetYear, adjustedMonth + 1, 0);
      
      // Find all occurrences of the target weekday in this month
      for (let day = 1; day <= lastDay.getDate(); day++) {
        const currentDate = new Date(targetYear, adjustedMonth, day);
        
        // Convert our day_of_week (0=Monday) to JavaScript's getDay() (0=Sunday, 1=Monday)
        // Our backend: Monday=0, Tuesday=1, ..., Sunday=6
        // JavaScript: Sunday=0, Monday=1, Tuesday=2, ..., Saturday=6
        const jsWeekday = dayOfWeek === 6 ? 0 : dayOfWeek + 1;
        
        // Check if this date matches the target weekday and is not in the past
        if (currentDate.getDay() === jsWeekday && currentDate >= new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
          // Build YYYY-MM-DD from LOCAL components. toISOString() converts local
          // midnight to UTC, shifting the date one day earlier in positive-offset
          // timezones (e.g. Poland), which broke backend date validation.
          const yyyy = currentDate.getFullYear();
          const mm = String(currentDate.getMonth() + 1).padStart(2, '0');
          const dd = String(currentDate.getDate()).padStart(2, '0');
          dates.push({
            value: `${yyyy}-${mm}-${dd}`, // YYYY-MM-DD format (local)
            label: currentDate.toLocaleDateString('pl-PL', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
          });
        }
      }
    }
    
    return dates;
  };

  const handleOpenCreateActualLesson = async (constLesson) => {
    setOpeningConstLessonId(constLesson.id);
    setSelectedConstLesson(constLesson);
    
    // Calculate available dates for this weekday
    const dates = getAvailableDatesForWeekday(constLesson.day_of_week);
    setAvailableDates(dates);
    
    setActualLessonData({
      lesson_dates: [],
      user_ids: [],
      is_free: constLesson.is_free || false,
      payment_on_site: constLesson.payment_on_site || false,
      payment_only_online: constLesson.payment_only_online || false,
      online_payment_amount: constLesson.online_payment_amount || null,
      on_site_payment_amount: constLesson.on_site_payment_amount || null
    });
    setCreationProgress(null);
    
    // Fetch const lesson users and populate the lesson users table
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/const-lessons/${constLesson.id}/users`,
        { headers }
      );
      if (response.status === 200) {
        // Only include users who are currently signed
        const signedUsers = response.data
          .filter(constUser => constUser.is_signed_for_this_time)
          .map(constUser => ({
            id: constUser.user_id,
            full_name: constUser.user_full_name,
            email: constUser.user_email,
            included: true // All signed users are included by default
          }));
        
        setLessonUsersForCreation(signedUsers);
        
        // Fetch passes for all signed users
        const signedUserIds = signedUsers.map(user => user.id);
        await fetchUserPasses(signedUserIds, constLesson.lesson_type_id);
      }
    } catch (error) {
      console.error("Error fetching const lesson users:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do pobrania użytkowników lekcji");
      }
      setLessonUsersForCreation([]);
    } finally {
      setOpeningConstLessonId(null);
    }
    
    setCreateActualLessonDialogOpen(true);
  };

  // Build the batch payload used by commit-lessons.
  const _buildBatchPayload = () => {
    const datesToCreate = actualLessonData.lesson_dates;
    const includedUserIds = lessonUsersForCreation
      .filter(user => user.included)
      .map(user => user.id);
    // Tri-state per user:
    //  - 'auto' / undefined → omit from the array so the backend auto-picks an
    //    eligible pass per date (can spread across several of the user's passes).
    //  - 'none'             → explicit "no pass": enrol as unpaid (debt).
    //  - a pass id          → pin that specific pass (debt fallback once used up).
    const userPassSelectionsArray = includedUserIds
      .map(userId => {
        const selection = userPassSelections[userId];
        if (selection === undefined || selection === null || selection === 'auto') {
          return null;
        }
        if (selection === 'none') {
          return { user_id: userId, pass_id: null };
        }
        return { user_id: userId, pass_id: selection };
      })
      .filter(Boolean);
    return {
      lesson_dates: datesToCreate,
      user_ids: includedUserIds,
      user_pass_selections: userPassSelectionsArray,
      is_free: actualLessonData.is_free,
      payment_on_site: actualLessonData.payment_on_site,
      payment_only_online: actualLessonData.payment_only_online,
      online_payment_amount: actualLessonData.online_payment_amount,
      on_site_payment_amount: actualLessonData.on_site_payment_amount,
    };
  };

  // Reset the "Do grafiku" form once a batch has been committed.
  const _resetCreateLessonForm = () => {
    setCreateActualLessonDialogOpen(false);
    setActualLessonData({
      lesson_dates: [],
      user_ids: [],
      is_free: null,
      payment_on_site: null,
      payment_only_online: null
    });
    setAvailableDates([]);
    setLessonUsersForCreation([]);
    setUserPasses({});
    setUserPassSelections({});
    setCreationProgress(null);
  };

  // Step 1 — dry-run preview. Calls plan-lessons (nothing is persisted or
  // emailed) so the admin can review the exact pass/debt allocation before
  // committing. The reviewed payload is captured for the confirm step.
  const handleCreateActualLesson = async () => {
    try {
      setIsCreatingActualLesson(true);
      setError('');

      const datesToCreate = actualLessonData.lesson_dates;
      if (datesToCreate.length === 0) return;

      const payload = _buildBatchPayload();
      const headers = await getAuthHeaders();

      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/const-lessons/${selectedConstLesson.id}/plan-lessons`,
        payload,
        { headers }
      );

      const plans = resp.data?.per_lesson_plans || [];
      const lessonTypeName = selectedConstLesson?.lesson_type?.name || "";

      // Dates that don't already exist become preview rows. No lesson_id yet —
      // nothing has been created, so the dialog hides committed-only actions.
      const perLessonReports = plans
        .filter((p) => !p.already_exists)
        .map((p) => ({
          lesson_id: null,
          lesson_start: p.lesson_start,
          lesson_type_name: p.lesson_type_name || lessonTypeName,
          results: p.results || [],
        }));

      // Warn about dates that would be skipped because the lesson already exists.
      const existingDates = plans.filter((p) => p.already_exists);
      if (existingDates.length > 0) {
        const labels = existingDates.map((p) => {
          const d = new Date(p.lesson_date + "T00:00:00");
          return d.toLocaleDateString("pl-PL");
        }).join(", ");
        setError(`Te daty już istnieją i zostaną pominięte: ${labels}`);
      }

      if (perLessonReports.length > 0) {
        setCreationReports(perLessonReports);
        setPendingBatchPayload(payload);
        setReportDialogMode("preview");
        setReportDialogOpen(true);
      } else if (existingDates.length === 0) {
        setError("Brak dat do utworzenia.");
      }
    } catch (error) {
      console.error("Error planning lessons:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do utworzenia lekcji");
      } else {
        setError(error.response?.data?.detail || 'Nie udało się przygotować podglądu');
      }
    } finally {
      setIsCreatingActualLesson(false);
    }
  };

  // Step 2 — real commit. Runs commit-lessons with the exact payload the admin
  // reviewed in the preview, then switches the open dialog to "committed" mode
  // so the admin can act on the real enrollments (mark paid, resend email).
  const handleConfirmCommit = async () => {
    if (!pendingBatchPayload || !selectedConstLesson) return;
    try {
      setCommitBusy(true);
      setError('');

      const headers = await getAuthHeaders();

      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/const-lessons/${selectedConstLesson.id}/commit-lessons`,
        pendingBatchPayload,
        { headers }
      );

      const committed = resp.data?.per_lesson_results || [];
      const failedDates = resp.data?.failed_dates || [];

      // Single-confirmation flow: commit-lessons already sent the confirmation
      // and payment-link emails. Close the dialog and report the outcome — no
      // second "wyślij e-mail z linkiem" step.
      setReportDialogOpen(false);
      setCreationReports([]);
      setReportDialogMode("committed");
      setPendingBatchPayload(null);

      if (failedDates.length > 0) {
        const labels = failedDates.map((f) => {
          const d = new Date(f.lesson_date + "T00:00:00");
          return `${d.toLocaleDateString("pl-PL")}: ${f.error}`;
        }).join("\n");
        setError(`Niektóre daty nie zostały utworzone:\n${labels}`);
      }
      if (committed.length > 0) {
        setSuccess(`Utworzono ${committed.length} lekcji pomyślnie`);
      }

      _resetCreateLessonForm();
    } catch (error) {
      console.error("Error creating lessons:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do utworzenia lekcji");
      } else {
        setError(error.response?.data?.detail || 'Nie udało się utworzyć lekcji');
      }
    } finally {
      setCommitBusy(false);
    }
  };

  const handleToggleUserInLesson = (userId) => {
    setLessonUsersForCreation(prev => 
      prev.map(user => 
        user.id === userId ? { ...user, included: !user.included } : user
      )
    );
  };

  const handleAddUserToLessonCreation = async (user) => {
    const userAlreadyExists = lessonUsersForCreation.some(u => u.id === user.id);
    if (!userAlreadyExists) {
      const newUserList = [
        ...lessonUsersForCreation,
        {
          id: user.id,
          full_name: user.full_name,
          email: user.email,
          included: true
        }
      ];
      
      setLessonUsersForCreation(newUserList);
      
      // Fetch passes for all users including the new one
      const allUserIds = newUserList.map(u => u.id);
      await fetchUserPasses(allUserIds, selectedConstLesson?.lesson_type_id);
    }
  };

  const handlePassSelectionChange = (userId, passId) => {
    setUserPassSelections(prev => ({
      ...prev,
      [userId]: passId
    }));
  };

  const handleRemoveUserFromLessonCreation = (userId) => {
    setLessonUsersForCreation(prev => 
      prev.filter(user => user.id !== userId)
    );
  };

  // Available users (not already in the lesson creation table)
  const availableUsers = allUsers.filter(user => 
    !lessonUsersForCreation.some(lessonUser => lessonUser.id === user.id)
  );

  // Available users (not already in the const lesson)
  const availableUsersForConstLesson = allUsers.filter(user => 
    !constLessonUsers.some(constUser => constUser.user_id === user.id)
  );

  // Group const lessons by day for mobile view
  const groupConstLessonsByDay = () => {
    const dayNames = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
    const grouped = {};
    
    filteredConstLessons.forEach(lesson => {
      const dayName = dayNames[lesson.day_of_week];
      if (!grouped[dayName]) {
        grouped[dayName] = [];
      }
      grouped[dayName].push(lesson);
    });
    
    // Sort lessons within each day by time
    Object.keys(grouped).forEach(day => {
      grouped[day].sort((a, b) => {
        if (a.hour !== b.hour) return a.hour - b.hour;
        return a.minute - b.minute;
      });
    });
    
    return grouped;
  };

  // Render mobile view: cards grouped by day
  const renderMobileConstLessons = () => {
    const lessonsByDay = groupConstLessonsByDay();
    const dayOrder = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
    
    return (
      <Stack spacing={3}>
        {dayOrder.map(dayName => {
          const dayLessons = lessonsByDay[dayName];
          if (!dayLessons || dayLessons.length === 0) return null;
          
          return (
            <Box key={dayName}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  pb: 1, 
                  borderBottom: '2px solid #e0e0e0',
                  fontWeight: 600,
                  color: '#333'
                }}
              >
                {dayName}
              </Typography>
              <Stack spacing={2}>
                {dayLessons.map(constLesson => {
                  const start = `${constLesson.hour.toString().padStart(2, '0')}:${constLesson.minute.toString().padStart(2, '0')}`;
                  const endHour = Math.floor((constLesson.hour * 60 + constLesson.minute + constLesson.duration_minutes) / 60) % 24;
                  const endMinute = (constLesson.minute + constLesson.duration_minutes) % 60;
                  const end = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
                  const maxPeople = constLesson.lesson_type?.max_people || 'Brak limitu';
                  
                  return (
                    <Card 
                      key={constLesson.id}
                      sx={{ 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        borderRadius: 2,
                        '&:active': {
                          transform: 'scale(0.98)',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.15)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 1,
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '1rem',
                            textAlign: 'center',
                            mb: 1.5,
                            ...getLessonTypeStyle(constLesson.lesson_type?.bg_color),
                          }}
                        >
                          {getLessonTypeName(constLesson.lesson_type_id, constLesson)}
                        </Box>
                        
                        <Typography 
                          variant="body2" 
                          sx={{ mb: 0.5, fontWeight: 500, textAlign: 'center' }}
                        >
                          {start} - {end} ({constLesson.duration_minutes} min)
                        </Typography>
                        
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ display: 'block', textAlign: 'center', mb: 2 }}
                        >
                          Max osób: {maxPeople}
                        </Typography>
                        
                        <Stack spacing={1}>
                          <Button
                            onClick={() => handleViewUsers(constLesson)}
                            startIcon={<PeopleIcon />}
                            variant="outlined"
                            fullWidth
                            sx={{
                              minHeight: 44,
                              fontSize: '0.875rem',
                              textTransform: 'none',
                            }}
                          >
                            Użytkownicy
                          </Button>
                          
                          <Button
                            onClick={() => handleOpenCreateActualLesson(constLesson)}
                            startIcon={openingConstLessonId === constLesson.id
                              ? <CircularProgress size={16} color="inherit" />
                              : <AddIcon />}
                            disabled={openingConstLessonId === constLesson.id}
                            color="success"
                            variant="outlined"
                            fullWidth
                            sx={{
                              minHeight: 44,
                              fontSize: '0.875rem',
                              textTransform: 'none',
                            }}
                          >
                            Dodaj Do Grafiku
                          </Button>
                          
                          <Stack direction="row" spacing={1}>
                            <Button
                              onClick={() => handleOpenEditLesson(constLesson)}
                              startIcon={<EditIcon />}
                              color="primary"
                              variant="outlined"
                              fullWidth
                              sx={{
                                minHeight: 44,
                                fontSize: '0.875rem',
                                textTransform: 'none',
                              }}
                            >
                              Edytuj
                            </Button>
                            <Button
                              onClick={() => handleDeleteConstLesson(constLesson.id)}
                              startIcon={<DeleteIcon />}
                              color="error"
                              variant="outlined"
                              fullWidth
                              sx={{
                                minHeight: 44,
                                fontSize: '0.875rem',
                                textTransform: 'none',
                              }}
                            >
                              Usuń
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  };

  return (
    <Box id="const-lessons-admin">
      {/* Header */}
      <Box 
        display="flex" 
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between" 
        alignItems={{ xs: "stretch", sm: "center" }} 
        mb={3}
        gap={2}
      >
        <Box>
          <Typography 
            variant="h5" 
            component="h2" 
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
          >
            Zarządzanie Stałymi Zajęciami
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Twórz i zarządzaj szablonem zajęć, które powtarzają się w określonych dniach tygodnia
          </Typography>
        </Box>
        <Button
          onClick={() => {
            setCreateLocationFilter(selectedLocationId || '');
            setCreateLessonDialogOpen(true);
          }}
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          size="large"
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 2, sm: 3 },
            whiteSpace: 'nowrap',
            minWidth: { sm: 'fit-content' },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Utwórz Stałą Lekcję
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            Utwórz Lekcję
          </Box>
        </Button>
      </Box>

      {/* Location Tabs */}
      {lessonTypeLocations.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={selectedLocationIndex}
            onChange={(_, newIndex) => setSelectedLocationIndex(newIndex)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 42,
              },
            }}
          >
            <Tab
              label="Wszystkie lokalizacje"
              icon={<LocationIcon fontSize="small" />}
              iconPosition="start"
            />
            {lessonTypeLocations
              .sort((a, b) => a.name.localeCompare(b.name, "pl"))
              .map((location) => (
                <Tab
                  key={location.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {location.name}
                      {!location.show_in_timetable && (
                        <VisibilityOffIcon fontSize="small" sx={{ color: 'text.disabled', ml: 0.5 }} titleAccess="Ukryta w grafiku publicznym" />
                      )}
                    </Box>
                  }
                />
              ))}
          </Tabs>
        </Box>
      )}

      {/* Success/Error Messages */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Box textAlign="center">
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">
              Ładowanie stałych zajęć...
            </Typography>
          </Box>
        </Box>
      ) : constLessons.length > 0 ? (
        isMobile ? (
          renderMobileConstLessons()
        ) : (
          <Box sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 2, 
            overflow: 'hidden',
            backgroundColor: 'white'
          }}>
            <div className="table-responsive" style={{ overflowX: 'auto' }}>
              <table 
                className="table table-bordered text-center white-table" 
                style={{ 
                  marginBottom: 0,
                  minWidth: '800px'
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th className="text-uppercase" style={{ padding: '16px', fontWeight: 600, color: '#333', minWidth: '100px' }}>
                      Poniedziałek
                    </th>
                    <th className="text-uppercase" style={{ padding: '16px', fontWeight: 600, color: '#333', minWidth: '100px' }}>
                      Wtorek
                    </th>
                    <th className="text-uppercase" style={{ padding: '16px', fontWeight: 600, color: '#333', minWidth: '100px' }}>
                      Środa
                    </th>
                    <th className="text-uppercase" style={{ padding: '16px', fontWeight: 600, color: '#333', minWidth: '100px' }}>
                      Czwartek
                    </th>
                    <th className="text-uppercase" style={{ padding: '16px', fontWeight: 600, color: '#333', minWidth: '100px' }}>
                      Piątek
                    </th>
                    <th className="text-uppercase" style={{ padding: '16px', fontWeight: 600, color: '#333', minWidth: '100px' }}>
                      Sobota
                    </th>
                    <th className="text-uppercase" style={{ padding: '16px', fontWeight: 600, color: '#333', minWidth: '100px' }}>
                      Niedziela
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(organizeConstLessonsForTimetable())
                    .sort((a, b) => {
                      const [hourA, minuteA] = a.split(':').map(Number);
                      const [hourB, minuteB] = b.split(':').map(Number);
                      
                      if (hourA !== hourB) {
                        return hourA - hourB;
                      }
                      return minuteA - minuteB;
                    })
                    .map((timeSlot) => {
                      const lessonsAtTime = organizeConstLessonsForTimetable()[timeSlot];
                      return (
                        <SingleConstLessonHour
                          key={timeSlot}
                          hour={lessonsAtTime}
                          timeSlot={timeSlot}
                        />
                      );
                    })}
                </tbody>
              </table>
            </div>
          </Box>
        )
      ) : (
        <Box sx={{ 
          p: 6, 
          textAlign: 'center', 
          border: '2px dashed #e0e0e0', 
          borderRadius: 2,
          backgroundColor: '#fafafa'
        }}>
          <AddIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Brak stałych zajęć
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Nie znaleziono żadnych stałych zajęć. Utwórz pierwszą stałą lekcję, aby zacząć.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => {
              setCreateLocationFilter(selectedLocationId || '');
              setCreateLessonDialogOpen(true);
            }}
            startIcon={<AddIcon />}
            size="large"
          >
            Utwórz Pierwszą Stałą Lekcję
          </Button>
        </Box>
      )}

      {/* Dialog for Creating New Const Lesson */}
      <Dialog
        open={createLessonDialogOpen}
        onClose={() => setCreateLessonDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Utwórz Nową Stałą Lekcję</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Lokalizacja</InputLabel>
            <Select
              value={createLocationFilter}
              onChange={(e) => {
                setCreateLocationFilter(e.target.value);
                setNewLessonData(prev => ({ ...prev, lesson_type_id: '' }));
              }}
              label="Lokalizacja"
            >
              <MenuItem value="">Wszystkie lokalizacje</MenuItem>
              {lessonTypeLocations
                .sort((a, b) => a.name.localeCompare(b.name, "pl"))
                .map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Typ Lekcji</InputLabel>
            <Select
              value={newLessonData.lesson_type_id}
              onChange={(e) => setNewLessonData(prev => ({ ...prev, lesson_type_id: e.target.value }))}
              label="Typ Lekcji"
            >
              {lessonTypes
                .filter(lt => !createLocationFilter || lt.location_id === createLocationFilter)
                .sort((a, b) => {
                  const nameA = a.friendly_name || a.name;
                  const nameB = b.friendly_name || b.name;
                  return nameA.localeCompare(nameB);
                })
                .map((lessonType) => (
                  <MenuItem key={lessonType.id} value={lessonType.id}>
                    {lessonType.friendly_name || lessonType.name} - Max osób: {lessonType.max_people || 'Brak limitu'}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Trener (opcjonalnie)</InputLabel>
            <Select
              value={newLessonData.trainer_id || ''}
              onChange={(e) => setNewLessonData(prev => ({ ...prev, trainer_id: e.target.value || null }))}
              label="Trener (opcjonalnie)"
            >
              <MenuItem value="">
                <em>Brak przypisanego trenera</em>
              </MenuItem>
              {trainers.map((trainer) => (
                <MenuItem key={trainer.id} value={trainer.id}>
                  {trainer.full_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Dzień tygodnia</InputLabel>
            <Select
              value={newLessonData.day_of_week}
              onChange={(e) => setNewLessonData(prev => ({ ...prev, day_of_week: e.target.value }))}
              label="Dzień tygodnia"
            >
              <MenuItem value={0}>Poniedziałek</MenuItem>
              <MenuItem value={1}>Wtorek</MenuItem>
              <MenuItem value={2}>Środa</MenuItem>
              <MenuItem value={3}>Czwartek</MenuItem>
              <MenuItem value={4}>Piątek</MenuItem>
              <MenuItem value={5}>Sobota</MenuItem>
              <MenuItem value={6}>Niedziela</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Godzina rozpoczęcia"
            type="time"
            value={`${newLessonData.hour.toString().padStart(2, '0')}:${newLessonData.minute.toString().padStart(2, '0')}`}
            onChange={(e) => {
              const [hour, minute] = e.target.value.split(':').map(Number);
              setNewLessonData(prev => ({
                ...prev,
                hour: hour || 0,
                minute: minute || 0
              }));
            }}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              step: 900, // 15 minutes in seconds
            }}
          />

          <TextField
            label="Długość lekcji (minuty)"
            type="number"
            value={newLessonData.duration_minutes}
            onChange={(e) => setNewLessonData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 50 }))}
            fullWidth
            margin="normal"
            inputProps={{
              min: 1,
              step: 5
            }}
            helperText="Wprowadź długość lekcji w minutach (np. 50, 60, 90)"
          />

          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Opcje płatności:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newLessonData.is_free}
                  onChange={(e) => setNewLessonData(prev => ({ ...prev, is_free: e.target.checked }))}
                />
              }
              label="Zajęcia bezpłatne"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newLessonData.payment_on_site}
                  onChange={(e) => setNewLessonData(prev => ({ ...prev, payment_on_site: e.target.checked }))}
                />
              }
              label="Płatność tylko na miejscu "
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newLessonData.payment_only_online}
                  onChange={(e) => setNewLessonData(prev => ({ ...prev, payment_only_online: e.target.checked }))}
                />
              }
              label="Tylko płatność online"
            />
          </Box>

          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Opcje zapisu:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newLessonData.sign_up_disabled}
                  onChange={(e) => setNewLessonData(prev => ({ ...prev, sign_up_disabled: e.target.checked }))}
                />
              }
              label="Wyłącz zapisy dla użytkowników (🔒)"
            />
          </Box>

          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Płatność podzielona (opcjonalnie):
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Jeśli chcesz, aby użytkownicy płacili część online, a część na miejscu, wypełnij oba pola poniżej.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Kwota online (PLN)"
                type="number"
                value={newLessonData.online_payment_amount || ''}
                onChange={(e) => setNewLessonData(prev => ({ ...prev, online_payment_amount: e.target.value ? parseInt(e.target.value) : null }))}
                fullWidth
                inputProps={{
                  min: 20,
                  step: 1
                }}
                helperText="Min. 20 PLN"
              />
              <TextField
                label="Kwota na miejscu (PLN)"
                type="number"
                value={newLessonData.on_site_payment_amount || ''}
                onChange={(e) => setNewLessonData(prev => ({ ...prev, on_site_payment_amount: e.target.value ? parseInt(e.target.value) : null }))}
                fullWidth
                inputProps={{
                  min: 1,
                  step: 1
                }}
                helperText="Min. 1 PLN"
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateLessonDialogOpen(false)} color="error">
            Anuluj
          </Button>
          <Button
            onClick={handleCreateConstLesson}
            color="primary"
            disabled={isCreatingLesson || !newLessonData.lesson_type_id}
          >
            {isCreatingLesson ? <CircularProgress size={24} /> : "Utwórz"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for Editing Const Lesson */}
      <Dialog
        open={editLessonDialogOpen}
        onClose={() => setEditLessonDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Edytuj Stałą Lekcję</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Lokalizacja</InputLabel>
            <Select
              value={editLocationFilter}
              onChange={(e) => {
                setEditLocationFilter(e.target.value);
                setEditLessonData(prev => ({ ...prev, lesson_type_id: '' }));
              }}
              label="Lokalizacja"
            >
              <MenuItem value="">Wszystkie lokalizacje</MenuItem>
              {lessonTypeLocations
                .sort((a, b) => a.name.localeCompare(b.name, "pl"))
                .map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Typ Lekcji</InputLabel>
            <Select
              value={editLessonData.lesson_type_id}
              onChange={(e) => setEditLessonData(prev => ({ ...prev, lesson_type_id: e.target.value }))}
              label="Typ Lekcji"
            >
              {lessonTypes
                .filter(lt => !editLocationFilter || lt.location_id === editLocationFilter)
                .sort((a, b) => {
                  const nameA = a.friendly_name || a.name;
                  const nameB = b.friendly_name || b.name;
                  return nameA.localeCompare(nameB);
                })
                .map((lessonType) => (
                  <MenuItem key={lessonType.id} value={lessonType.id}>
                    {lessonType.friendly_name || lessonType.name} - Max osób: {lessonType.max_people || 'Brak limitu'}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Trener</InputLabel>
            <Select
              value={editLessonData.trainer_id || ''}
              onChange={(e) => setEditLessonData(prev => ({ ...prev, trainer_id: e.target.value || null }))}
              label="Trener"
            >
              <MenuItem value="">
                <em>Brak przypisanego trenera</em>
              </MenuItem>
              {trainers.map((trainer) => (
                <MenuItem key={trainer.id} value={trainer.id}>
                  {trainer.full_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Dzień tygodnia</InputLabel>
            <Select
              value={editLessonData.day_of_week}
              onChange={(e) => setEditLessonData(prev => ({ ...prev, day_of_week: e.target.value }))}
              label="Dzień tygodnia"
            >
              <MenuItem value={0}>Poniedziałek</MenuItem>
              <MenuItem value={1}>Wtorek</MenuItem>
              <MenuItem value={2}>Środa</MenuItem>
              <MenuItem value={3}>Czwartek</MenuItem>
              <MenuItem value={4}>Piątek</MenuItem>
              <MenuItem value={5}>Sobota</MenuItem>
              <MenuItem value={6}>Niedziela</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Godzina rozpoczęcia"
            type="time"
            value={`${editLessonData.hour.toString().padStart(2, '0')}:${editLessonData.minute.toString().padStart(2, '0')}`}
            onChange={(e) => {
              const [hour, minute] = e.target.value.split(':').map(Number);
              setEditLessonData(prev => ({
                ...prev,
                hour: hour || 0,
                minute: minute || 0
              }));
            }}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              step: 900, // 15 minutes in seconds
            }}
          />

          <TextField
            label="Długość lekcji (minuty)"
            type="number"
            value={editLessonData.duration_minutes}
            onChange={(e) => setEditLessonData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 50 }))}
            fullWidth
            margin="normal"
            inputProps={{
              min: 1,
              step: 5
            }}
            helperText="Wprowadź długość lekcji w minutach (np. 50, 60, 90)"
          />

          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Opcje płatności:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editLessonData.is_free}
                  onChange={(e) => setEditLessonData(prev => ({ ...prev, is_free: e.target.checked }))}
                />
              }
              label="Zajęcia bezpłatne"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editLessonData.payment_on_site}
                  onChange={(e) => setEditLessonData(prev => ({ ...prev, payment_on_site: e.target.checked }))}
                />
              }
              label="Płatność tylko na miejscu"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editLessonData.payment_only_online}
                  onChange={(e) => setEditLessonData(prev => ({ ...prev, payment_only_online: e.target.checked }))}
                />
              }
              label="Tylko płatność online"
            />
          </Box>

          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Opcje zapisu:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editLessonData.sign_up_disabled}
                  onChange={(e) => setEditLessonData(prev => ({ ...prev, sign_up_disabled: e.target.checked }))}
                />
              }
              label="Wyłącz zapisy dla użytkowników (🔒)"
            />
          </Box>

          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Płatność podzielona (opcjonalnie):
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Jeśli chcesz, aby użytkownicy płacili część online, a część na miejscu, wypełnij oba pola poniżej.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Kwota online (PLN)"
                type="number"
                value={editLessonData.online_payment_amount || ''}
                onChange={(e) => setEditLessonData(prev => ({ ...prev, online_payment_amount: e.target.value ? parseInt(e.target.value) : null }))}
                fullWidth
                inputProps={{
                  min: 20,
                  step: 1
                }}
                helperText="Min. 20 PLN"
              />
              <TextField
                label="Kwota na miejscu (PLN)"
                type="number"
                value={editLessonData.on_site_payment_amount || ''}
                onChange={(e) => setEditLessonData(prev => ({ ...prev, on_site_payment_amount: e.target.value ? parseInt(e.target.value) : null }))}
                fullWidth
                inputProps={{
                  min: 1,
                  step: 1
                }}
                helperText="Min. 1 PLN"
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditLessonDialogOpen(false)} color="error">
            Anuluj
          </Button>
          <Button
            onClick={handleUpdateConstLesson}
            color="primary"
            disabled={isEditingLesson || !editLessonData.lesson_type_id}
          >
            {isEditingLesson ? <CircularProgress size={24} /> : "Zapisz zmiany"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for Managing Users in Const Lesson */}
      <Dialog
        open={constLessonDialogOpen}
        onClose={() => setConstLessonDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Użytkownicy w Stałej Lekcji: {selectedConstLesson && getLessonTypeWithLocation(selectedConstLesson.lesson_type_id, selectedConstLesson)}
          <br />
          <Typography variant="subtitle2" color="text.secondary">
            {selectedConstLesson && formatConstLessonTime(selectedConstLesson)}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              onClick={() => setAddUserDialogOpen(true)}
              color="primary"
              variant="contained"
              startIcon={<PersonAddIcon />}
            >
              Dodaj Użytkownika
            </Button>
          </Box>

          {constLessonUsers.length > 0 ? (
            <List>
              {constLessonUsers.map((constUser) => (
                <ListItem key={constUser.id} divider>
                  <ListItemText
                    primary={constUser.user_full_name}
                    secondary={`${constUser.user_email} | Dodano: ${formatDateTime(constUser.created_at)}`}
                  />
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={constUser.is_signed_for_this_time ? "Zapisany" : "Nie zapisany"}
                      color={constUser.is_signed_for_this_time ? "success" : "default"}
                      size="small"
                    />
                    <IconButton
                      onClick={() => handleToggleUserSign(constUser.user_id)}
                      color={constUser.is_signed_for_this_time ? "warning" : "success"}
                      disabled={isUpdatingUser[constUser.user_id]}
                      size="small"
                      title={constUser.is_signed_for_this_time ? "Wypisz na ten raz" : "Zapisz na ten raz"}
                    >
                      {isUpdatingUser[constUser.user_id] ? (
                        <CircularProgress size={16} />
                      ) : constUser.is_signed_for_this_time ? (
                        <ToggleOffIcon />
                      ) : (
                        <ToggleOnIcon />
                      )}
                    </IconButton>
                  </Box>
                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={() => handleRemoveUserFromConstLesson(constUser.user_id)}
                      color="error"
                      title="Usuń ze stałej listy"
                    >
                      <PersonRemoveIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>Brak użytkowników w tej stałej lekcji.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConstLessonDialogOpen(false)} color="primary">
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for Adding User to Const Lesson */}
      <Dialog
        open={addUserDialogOpen}
        onClose={() => setAddUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Dodaj Użytkownika do Stałej Lekcji</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={availableUsersForConstLesson}
            getOptionLabel={(option) => `${option.full_name} (${option.email})`}
            onChange={(_, newValue) => setSelectedUserToAdd(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Wybierz Użytkownika"
                variant="outlined"
                fullWidth
                margin="normal"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserDialogOpen(false)} color="error">
            Anuluj
          </Button>
          <Button
            onClick={handleAddUserToConstLesson}
            color="primary"
            disabled={isAddingUser || !selectedUserToAdd}
          >
            {isAddingUser ? <CircularProgress size={24} /> : "Dodaj"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for Creating Actual Lesson from Const Lesson */}
      <Dialog
        open={createActualLessonDialogOpen}
        onClose={() => {
          if (!isCreatingActualLesson) {
            setCreateActualLessonDialogOpen(false);
            setUserPasses({});
            setUserPassSelections({});
            setCreationProgress(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Dodaj Lekcję do Grafiku: {selectedConstLesson && getLessonTypeWithLocation(selectedConstLesson.lesson_type_id, selectedConstLesson)}
          <br />
          <Typography variant="subtitle2" color="text.secondary">
            {selectedConstLesson && formatConstLessonTime(selectedConstLesson)}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Wybierz daty lekcji</InputLabel>
            <Select
              multiple
              value={actualLessonData.lesson_dates}
              onChange={(e) => {
                const sorted = [...e.target.value].sort();
                setActualLessonData(prev => ({ ...prev, lesson_dates: sorted }));
              }}
              label="Wybierz daty lekcji"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const dateOption = availableDates.find(d => d.value === value);
                    return (
                      <Chip key={value} label={dateOption ? dateOption.label : value} size="small" />
                    );
                  })}
                </Box>
              )}
            >
              {availableDates.map((dateOption) => (
                <MenuItem key={dateOption.value} value={dateOption.value}>
                  <Checkbox checked={actualLessonData.lesson_dates.indexOf(dateOption.value) > -1} />
                  {dateOption.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {availableDates.length === 0 && (
            <Typography color="warning.main" sx={{ mb: 2 }}>
              Brak dostępnych dat dla tego dnia tygodnia w bieżącym i następnym miesiącu.
            </Typography>
          )}

          {creationProgress && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Tworzenie lekcji: {creationProgress.completed}/{creationProgress.total}
              {creationProgress.succeeded > 0 && ` (✓ ${creationProgress.succeeded}`}
              {creationProgress.failed > 0 && `, ✗ ${creationProgress.failed}`}
              {creationProgress.succeeded > 0 && ')'}
            </Alert>
          )}


          <Typography variant="body2" sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <strong>Lokalizacja:</strong> {selectedConstLesson && (() => {
              const lessonType = lessonTypes.find(lt => lt.id === selectedConstLesson.lesson_type_id);
              if (!lessonType?.location) return 'Brak lokalizacji';

              const locationName = lessonType.location.name || 'Nieznana lokalizacja';
              const locationAddress = typeof lessonType.location.address === 'string'
                ? lessonType.location.address
                : '';

              return locationName + (locationAddress ? ` (${locationAddress})` : '');
            })()}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Opcje płatności (nadpisują ustawienia z szablonu):
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={actualLessonData.is_free || false}
                  onChange={(e) => setActualLessonData(prev => ({ ...prev, is_free: e.target.checked }))}
                />
              }
              label="Zajęcia bezpłatne"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={actualLessonData.payment_on_site || false}
                  onChange={(e) => setActualLessonData(prev => ({ ...prev, payment_on_site: e.target.checked }))}
                />
              }
              label="Płatność tylko na miejscu"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={actualLessonData.payment_only_online || false}
                  onChange={(e) => setActualLessonData(prev => ({ ...prev, payment_only_online: e.target.checked }))}
                />
              }
              label="Tylko płatność online"
            />
          </Box>

          {selectedConstLesson && (() => {
            const lessonType = lessonTypes.find(lt => lt.id === selectedConstLesson.lesson_type_id);
            const lessonPrice = lessonType?.price || 0;
            const onlineAmount = actualLessonData.online_payment_amount || 0;
            const onSiteAmount = actualLessonData.on_site_payment_amount || 0;
            const totalSplit = onlineAmount + onSiteAmount;
            const hasSplitPayment = actualLessonData.online_payment_amount !== null && actualLessonData.on_site_payment_amount !== null;
            const sumMatches = hasSplitPayment && totalSplit === lessonPrice;

            return (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                  <strong>Cena zajęć:</strong> {lessonPrice} PLN
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Płatność podzielona (opcjonalnie):
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Jeśli chcesz, aby użytkownicy płacili część online, a część na miejscu, wypełnij oba pola poniżej.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={1}>
                  <TextField
                    label="Kwota online (PLN)"
                    type="number"
                    size="small"
                    value={actualLessonData.online_payment_amount || ''}
                    onChange={(e) => setActualLessonData(prev => ({ ...prev, online_payment_amount: e.target.value ? parseInt(e.target.value) : null }))}
                    fullWidth
                    inputProps={{
                      min: 20,
                      step: 1
                    }}
                    helperText="Min. 20 PLN"
                  />
                  <TextField
                    label="Kwota na miejscu (PLN)"
                    type="number"
                    size="small"
                    value={actualLessonData.on_site_payment_amount || ''}
                    onChange={(e) => setActualLessonData(prev => ({ ...prev, on_site_payment_amount: e.target.value ? parseInt(e.target.value) : null }))}
                    fullWidth
                    inputProps={{
                      min: 1,
                      step: 1
                    }}
                    helperText="Min. 1 PLN"
                  />
                </Stack>
                {hasSplitPayment && (
                  <Alert severity={sumMatches ? "success" : "error"} sx={{ mt: 1 }}>
                    {sumMatches 
                      ? `✓ Suma płatności (${onlineAmount} + ${onSiteAmount} = ${totalSplit} PLN) jest równa cenie zajęć`
                      : `Suma płatności online (${onlineAmount} PLN) i płatności na miejscu (${onSiteAmount} PLN) musi być równa cenie zajęć (${lessonPrice} PLN). Aktualnie: ${totalSplit} PLN`
                    }
                  </Alert>
                )}
              </Box>
            );
          })()}

          <Typography variant="h6" mt={2} mb={1}>
            Użytkownicy na lekcji:
          </Typography>
          
          {lessonUsersForCreation.length > 0 ? (
            <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1 }}>
              {lessonUsersForCreation.map((user) => (
                <ListItem key={user.id} dense sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                  <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                    <Box flex={1}>
                      <Typography variant="body2" fontWeight="medium">{user.full_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={user.included ? "Będzie na lekcji" : "Pominięty"}
                        color={user.included ? "success" : "default"}
                        size="small"
                      />
                      <IconButton
                        onClick={() => handleToggleUserInLesson(user.id)}
                        color={user.included ? "warning" : "success"}
                        size="small"
                        title={user.included ? "Wyklucz z lekcji" : "Włącz do lekcji"}
                      >
                        {user.included ? <ToggleOffIcon /> : <ToggleOnIcon />}
                      </IconButton>
                      <IconButton
                        onClick={() => handleRemoveUserFromLessonCreation(user.id)}
                        color="error"
                        size="small"
                        title="Usuń z listy"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  {/* Pass Selection */}
                  {user.included && (
                    <Box mt={1} width="100%">
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Karnet</InputLabel>
                        <Select
                          value={userPassSelections[user.id] ?? 'auto'}
                          onChange={(e) => handlePassSelectionChange(user.id, e.target.value)}
                          label="Karnet"
                        >
                          <MenuItem value="auto">
                            <em>Automatycznie — użyj dostępnych karnetów</em>
                          </MenuItem>
                          <MenuItem value="none">
                            <em>Bez karnetu (płatność oddzielna / dług)</em>
                          </MenuItem>
                          {(userPasses[user.id] || []).map((pass) => {
                            const remainingLessons = pass.lesson_categories_for_pass?.reduce((total, category) => total + category.remaining_lessons, 0) || 0;
                            return (
                              <MenuItem key={pass.id} value={pass.id}>
                                {pass.pass_type_name} - ważny do {new Date(pass.valid_until).toLocaleDateString('pl-PL')} ({remainingLessons} lekcji)
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      {(userPassSelections[user.id] ?? 'auto') === 'auto' && userPasses[user.id]?.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                          System sam dobierze karnety (może użyć kilku w kolejnych terminach).
                        </Typography>
                      )}
                      {userPasses[user.id]?.length === 0 && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                          Użytkownik nie posiada aktywnych karnetów
                        </Typography>
                      )}
                    </Box>
                  )}
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', border: '1px solid #ddd', borderRadius: 1 }}>
              Brak użytkowników na tej lekcji
            </Typography>
          )}

          <Box mt={2} display="flex" alignItems="center" gap={2}>
            <Autocomplete
              options={availableUsers}
              getOptionLabel={(option) => `${option.full_name} (${option.email})`}
              onChange={(_, newValue) => {
                if (newValue) {
                  handleAddUserToLessonCreation(newValue);
                }
              }}
              value={null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dodaj użytkownika"
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 300 }}
                />
              )}
            />
          </Box>

          {actualLessonData.lesson_dates.length > 0 && (
            <Box mt={1}>
              <Typography variant="body2" color="text.secondary">
                {actualLessonData.lesson_dates.length === 1 ? 'Lekcja będzie zaplanowana na:' : `Lekcje będą zaplanowane na (${actualLessonData.lesson_dates.length} dat):`}
              </Typography>
              {actualLessonData.lesson_dates.map((d) => {
                const date = new Date(d + "T00:00:00");
                return (
                  <Typography key={d} variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    • {date.toLocaleDateString('pl-PL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    {' o '}{selectedConstLesson && `${selectedConstLesson.hour.toString().padStart(2, '0')}:${selectedConstLesson.minute.toString().padStart(2, '0')}`}
                  </Typography>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateActualLessonDialogOpen(false);
            setUserPasses({});
            setUserPassSelections({});
            setCreationProgress(null);
          }} color="error" disabled={isCreatingActualLesson}>
            Anuluj
          </Button>
          <Button
            onClick={handleCreateActualLesson}
            color="primary"
            disabled={
              isCreatingActualLesson || 
              actualLessonData.lesson_dates.length === 0
            }
          >
            {isCreatingActualLesson ? <CircularProgress size={24} /> : 
              actualLessonData.lesson_dates.length <= 1 
                ? `Utwórz (${lessonUsersForCreation.filter(user => user.included).length} os.)`
                : `Utwórz ${actualLessonData.lesson_dates.length} lekcji (${lessonUsersForCreation.filter(user => user.included).length} os.)`
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dry-run preview shown before committing. The admin reviews the exact
          pass/debt allocation, then "Potwierdź i utwórz" commits in a single
          step — emails are sent automatically by the backend. */}
      <CreationReportDialog
        open={reportDialogOpen}
        onClose={() => {
          setReportDialogOpen(false);
          setCreationReports([]);
          setReportDialogMode("committed");
          setPendingBatchPayload(null);
        }}
        perLessonReports={creationReports}
        mode={reportDialogMode}
        onCommit={handleConfirmCommit}
        commitBusy={commitBusy}
      />
    </Box>
  );
};

export default ConstLessonManagement;
